import { lineupStartOffsets, rotateToProduct, toLineupItem } from '@apm/contracts';
import type {
  AdPolicy,
  ChannelLineup,
  ChannelSummary,
  LineupItem,
  LiveEventSlot,
  OnairChannelLineup,
  PlaylistItem,
} from '@apm/contracts';

import { entryStartFor } from './last-items.js';
import type { LastItems } from './last-items.js';

/**
 * 채널 편성표의 판정 (v25, `docs/51`). **순수 함수만 둔다.**
 *
 * `localStorage` 를 직접 만지면 이 규칙들이 node 테스트에서 통째로 빠진다
 * (`channel-selection.ts`·`routing.ts`·`product-list.ts` 와 같은 자리). 저장소를 만지는
 * 것은 `favorite-channels.ts` 이고, 여기는 그것이 준 배열을 받기만 한다.
 */

/** 망가진 값이 무한히 자라지 않게. 채널이 이만큼 많아지면 그때 다시 본다 */
const FAVORITES_MAX = 32;

/**
 * 저장소에서 온 `unknown` 하나에서 유효한 채널 id 목록을 낸다. **여기가 유일한 검증 자리다.**
 *
 * 하는 일 다섯 — 배열이 아니면 `[]` · 문자열 아닌 원소 버림 · 빈 문자열 버림 · 중복 제거 ·
 * 상한 `FAVORITES_MAX`.
 *
 * ★ **「공개 목록에 없는 id 를 버린다」를 넣지 않는다.** 편성이 잠깐 빈 채널은
 * `/v1/channels` 에서 빠지고(절대 규칙 1 의 승계), 그날 편성표를 한 번 열면 **별이 조용히
 * 사라진다.** 증상은 「어제 별 붙인 채널이 오늘 없다」 하나이고 **다시 붙이면 되니까 아무도
 * 신고하지 않는다.** `resolveChannel` 과 같은 방침이다 — **표시가 거르고 저장이 기억한다.**
 */
export function normalizeFavorites(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const value of raw) {
    if (typeof value !== 'string' || value === '') continue;
    if (out.includes(value)) continue;
    out.push(value);
    if (out.length >= FAVORITES_MAX) break;
  }
  return out;
}

export function isFavorite(favorites: readonly string[], channelId: string): boolean {
  return favorites.includes(channelId);
}

export function toggleFavorite(favorites: readonly string[], channelId: string): string[] {
  if (channelId === '') return [...favorites];
  return isFavorite(favorites, channelId)
    ? favorites.filter((id) => id !== channelId)
    : normalizeFavorites([...favorites, channelId]);
}

/**
 * 채널 목록을 즐겨찾기와 나머지로 가른다. **안정 분할이다** — 원래 순서를 지킨다.
 *
 * ★ 즐겨찾기 추가 순서로 재배열하지 않는 것이 결정이다. 별을 누르면 그 행이 위 구역으로
 * 옮겨 가는데, **움직이는 행이 하나뿐**이어야 다음 방향키의 결과가 덜 놀랍다
 * (`docs/51` §8.3).
 */
export function partitionByFavorite(
  channels: readonly ChannelSummary[],
  favorites: readonly string[],
): { favorites: ChannelSummary[]; rest: ChannelSummary[] } {
  const fav: ChannelSummary[] = [];
  const rest: ChannelSummary[] = [];
  for (const channel of channels) {
    (isFavorite(favorites, channel.channelId) ? fav : rest).push(channel);
  }
  return { favorites: fav, rest };
}

/** 편성표 한 행 — 채널 카드(목록에서)와 편성(편성표에서)을 합친 것 */
export interface EpgRow {
  channel: ChannelSummary;
  /**
   * v28. ONAIR 채널의 편성 — **절대 시각이 들어 있는 유일한 자리다** (절대 규칙 75).
   *
   * **`lineup` 과 배타적이다** — 서버가 `GET /v1/channels/lineup` 에서 ONAIR 를 빼고,
   * ONAIR 서버가 개인화 채널을 모른다. 한 행이 둘 다 가지면 **화면이 순서축과 시각축을
   * 한 그림에 섞게 되고**, 그때 무엇을 그릴지는 렌더 순서가 정한다.
   *
   * 못 받았으면 `null` — 「방송 준비 중」 자리표시다(ONAIR 서버가 죽어도 행은 남는다).
   */
  onair: OnairChannelLineup | null;
  /** 편성표를 아직 못 받았거나 그 채널이 응답에 없으면 `null` — 화면이 자리표시를 그린다 */
  lineup: ChannelLineup | null;
  /**
   * v33. **이 채널의 라이브 이벤트 창들** — 앞으로 남은 것만, 시각 순 (`docs/67` §9).
   *
   * ★ **`lineup` 과 배타적이지 않다.** `onair` 는 여전히 배타적이고(그 행에는 순서축이
   * 없다), 이 행에는 **순서축(녹화)과 시각축(라이브)이 함께** 있다 — 그것이 v33 이 처음
   * 만드는 모양이고, 무엇을 어디 그릴지는 `liveSpansAt` 과 행의 두 칸이 정한다.
   *
   * 없으면 빈 배열이고 **그때 이 행은 지금까지와 한 픽셀도 다르지 않다.**
   */
  liveEvents: readonly LiveEventSlot[];
  /** `lineup.items` 와 같은 길이. 못 받았으면 빈 배열 */
  startOffsetsSec: number[];
  /**
   * **광고 토글까지 반영한** 정책. `applyCurrentRow` 가 다시 셀 때 이 값을 쓴다 —
   * 원본(`lineup.adPolicy`)을 쓰면 **꺼 놓은 광고가 그 한 행에서만 되살아난다.**
   */
  adPolicy: AdPolicy | null;
}

/**
 * 두 응답을 `channelId` 로 짝짓는다.
 *
 * ★ **채널 목록이 「무엇이 보이는가」의 권위다** (절대 규칙 1). 편성표에만 있고 목록에 없는
 * 채널은 **버린다** — 목록은 무인증이고 캐시되는 첫 그림이며, 편성표는 그 위에 덧그리는
 * 것이다. 반대로 짝이 없는 목록 항목은 **그대로 남는다** — 편성표가 실패해도 채널 목록은
 * 뜬다는 것이 D10 을 지키는 마지막 겹이다 (`docs/51` §2.4).
 *
 * `adsEnabled` 가 거짓이면 정책을 `null` 로 바꿔 넘긴다 — **끈 상태에서 광고를 더하면
 * 없는 시간을 말하는 화면이 된다**(`ProductListSheet` 가 `+광고 n분` 배지에 대해 이미
 * 그렇게 한다).
 */
export function toEpgRows(
  channels: readonly ChannelSummary[],
  lineups: readonly ChannelLineup[],
  options: {
    adsEnabled: boolean;
    lastItems?: LastItems;
    /**
     * v28. ONAIR 편성 — **앱이 ONAIR 서버에서 직접 받은 것**이다 (절대 규칙 75).
     * 셋째 출처이고, 없으면 그 행이 자리표시가 될 뿐이다.
     */
    onair?: ReadonlyMap<string, OnairChannelLineup>;
  } = { adsEnabled: true },
): EpgRow[] {
  const byId = new Map(lineups.map((l) => [l.channelId, l]));
  return channels.map((channel) => {
    /*
     * ★ v28 — **ONAIR 행이 먼저다.** 그 채널은 `GET /v1/channels/lineup` 에 애초에
     * 없으므로(`docs/57` §7.2) 아래 갈래로 가면 언제나 자리표시가 된다.
     */
    if (channel.delivery === 'onair') {
      return {
        channel,
        lineup: null,
        onair: options.onair?.get(channel.channelId) ?? null,
        // ONAIR 채널에는 라이브 이벤트가 없다 — 서버가 이미 편성대로 내보내고 있다
        // (`ONAIR_LIVE_NOT_ALLOWED`). 한 채널이 두 시각축을 갖지 않는다.
        liveEvents: [],
        startOffsetsSec: [],
        // 광고가 없다 (v28 범위 · 절대 규칙 76).
        adPolicy: null,
      };
    }
    const lineup = byId.get(channel.channelId) ?? null;
    if (lineup === null)
      return {
        channel,
        lineup: null,
        onair: null,
        liveEvents: [],
        startOffsetsSec: [],
        adPolicy: null,
      };
    const policy: AdPolicy | null = options.adsEnabled ? lineup.adPolicy : null;
    /*
     * ★ v26 — **홈쇼핑 행은 기기가 기억한 항목으로 돈다** (절대 규칙 68·70).
     *
     * **서버는 그 값을 모른다** — 기억은 기기에 있다(`docs/53` D1). 그대로 두면 카드에는
     * 편성 첫 상품이 「지금」으로 떠 있는데 눌러 들어가면 **기억한 다른 상품**이 나온다.
     * 화면에 아무 오류도 없고, 눌러 보기 전에는 갈리지 않는다.
     *
     * ★★ **회전이 오프셋보다 먼저다.** 뒤집으면 「약 n분 후」가 한 칸씩 어긋나는데,
     * 홈쇼핑은 상품이 몇 분이라 **그 어긋남이 작아서 아무도 신고하지 않는다.**
     *
     * 자물쇠 ②(`entryStartFor`)와 같은 판정을 여기서도 지난다 — 일반 채널의 행은
     * 돌지 않는다. 서버의 이어보기 회전이 이미 그쪽의 답이고, 두 회전이 겹치면
     * **이어보기 카드가 엉뚱한 자리에 붙는다.**
     */
    // 2026-09-10. **계정 행이 있으면 서버가 이미 돌렸다**(`resumeUpdatedAt`) — 기기의 기억은
    // 계정 행이 없을 때(비로그인)의 것이다(규칙 70 개정). 둘을 겹치면 화면과 재생이 갈린다.
    const remembered =
      lineup.resumeUpdatedAt !== null
        ? null
        : entryStartFor(channel.kind, options.lastItems ?? {}, channel.channelId);
    const items =
      remembered === null ? lineup.items : rotateToProduct(lineup.items, remembered);
    return {
      channel,
      onair: null,
      // v33. **서버가 준 그대로다** — 시각은 사람이 콘솔에 박은 값이고 화면이 계산하지
      // 않는다(절대 규칙 75 의 단서). 주소는 애초에 오지 않는다(규칙 68 의 단서).
      liveEvents: lineup.liveEvents,
      lineup: items === lineup.items ? lineup : { ...lineup, items },
      // 산술은 계약 한 곳이다 (절대 규칙 57). 여기서 다시 쓰지 않는다.
      startOffsetsSec: lineupStartOffsets(policy, items, lineup.resumePositionSec),
      adPolicy: policy,
    };
  });
}

/**
 * **지금 보고 있는 채널의 행만** 재생 중인 항목 기준으로 다시 세운다 (v25).
 *
 * ── 왜 필요한가 ──────────────────────────────────────────────
 *
 * 행의 첫 장은 **「이 채널로 가면 무엇이 나오는가」**다. 안 보고 있는 채널에서 그 답은
 * 이어보기 지점(서버 회전)이고, **보고 있는 채널에서 그 답은 「지금 재생 중인 것」**이다.
 *
 * 그것을 안 하면 한 행이 **「지금」을 두 번 주장한다** — 첫 장에 `epg.entryPoint`(「지금」),
 * 재생 중인 장에 `epg.nowPlaying`(「지금 재생 중」). 2026-08-31 무대에서 실제로 그렇게
 * 보였다.
 *
 * **그리고 상대 시각이 틀린다.** 첫 장 기준으로 세므로 셋째를 보고 있는데 넷째가
 * 「약 5분 후」다 — 실제로는 지금 것의 남은 시간이면 온다. **홈쇼핑에서 가장 크게
 * 어긋난다**(이어보기가 없어 서버가 회전을 안 하므로 첫 장이 언제나 편성 첫 항목이다).
 *
 * ── 왜 서버가 아니라 여기인가 ────────────────────────────────
 *
 * **서버는 지금 무엇이 재생 중인지 모른다.** 편성표 응답은 받아 온 순간의 스냅숏이고,
 * 재생 위치는 리시버만 안다. 그래서 이 한 행만 화면이 다시 세운다 — 그리고 **회전은
 * 서버가 쓰는 그 함수 그대로**다(`rotateToProduct` 를 계약으로 옮긴 이유).
 *
 * **이어보기 표시를 지운다**(`resumePositionSec: null`). 지금 보고 있는 채널에서
 * 「0:25부터 이어보기」는 뜻이 없고, 회전 뒤에는 그 표시가 **엉뚱한 카드**에 붙는다.
 */
export function applyCurrentRow(
  rows: readonly EpgRow[],
  current: {
    channelId: string;
    productId: string | null;
    elapsedSec: number;
    /**
     * v39. **리시버가 지금 들고 있는 재생 목록**(`view.items`). 재생 중인 회차가 편성표에 없을 때만 쓴다 — 비로그인으로
     * 편성 밖 라이브러리 회차를 보는 동안이 그것이다(서버는 그 시청자의 꼬리를 모른다 · `docs/81` §5.3). 카드 변환은
     * 계약의 `toLineupItem` 하나다 — 재생 필드가 떨어진다(규칙 105).
     */
    items?: readonly PlaylistItem[];
  } | null,
): EpgRow[] {
  if (current === null || current.productId === null) return [...rows];
  const productId = current.productId;
  return rows.map((row) => {
    if (row.channel.channelId !== current.channelId || row.lineup === null) return row;
    let items = rotateToProduct(row.lineup.items, productId);
    // v39. 편성표에 없다 — 리시버의 목록에 있으면 그것으로 세운다(라이브러리 꼬리 · 비로그인).
    if (items[0]?.productId !== productId && current.items?.some((i) => i.productId === productId)) {
      items = rotateToProduct(current.items.map(toLineupItem), productId);
    }
    // 회전이 안 됐다 — 재생 중인 것이 편성표에도 리시버의 목록에도 없다(편성이 그 사이에 바뀌었다).
    // **그대로 둔다.** 억지로 손대면 「지금」이 아무 데도 없는 행이 된다.
    if (items[0]?.productId !== productId) return row;
    return {
      ...row,
      lineup: { ...row.lineup, items, resumePositionSec: null },
      // 첫 장은 **남은 길이**다 — `lineupStartOffsets` 가 이미 그렇게 센다.
      startOffsetsSec: lineupStartOffsets(row.adPolicy, items, current.elapsedSec),
    };
  });
}

/**
 * 편성표에서 카드를 눌렀을 때 무엇을 할 것인가. **이 화면의 판정 전부가 여기 모인다** —
 * `resolveChannel`·`resolvePick` 이 그 전례다.
 *
 * ★★ **첫 장에 `startFromProductId` 를 주지 않는 것이 이 함수의 요점이다** (절대 규칙 68).
 * 주면 서버의 `resolveResume` 이 「고른 것」 갈래로 들어가 **`positionSec: 0`** 을 내고,
 * `reason: 'select'` 진행 보고가 그 0 으로 **저장된 지점을 덮는다 — 되돌릴 수 없다.**
 * 그러면 그 카드에 그린 「12:03부터 이어보기」가 **거짓말이 된다.**
 *
 * 그리고 부수 효과 하나가 공짜로 따라온다 — **첫 장을 누르는 것은 오늘의 채널 목록 항목을
 * 누르는 것과 바이트 단위로 같은 요청**이라, `verify-channels` 의 전환 검사가 밟는 경로가
 * 그대로다.
 */
export type EpgPick =
  /** 지금 보고 있는 그 항목이다. 시트만 닫는다 (`resolvePick` 의 D5 와 같은 뜻) */
  | { kind: 'same' }
  /**
   * v33-b. **같은 채널의 라이브 상자를 눌렀다** — 그 창에 합류한다(`receiver.joinLive`).
   * 목록을 고르며 포기했던 창이라도 다시 들어간다 — 한 채널 안에서 **오갈 수 있다**.
   */
  | { kind: 'live' }
  /**
   * v33-b. **바이패스 중에 지금 보던 녹화 회차를 눌렀다** — 창을 두고 **그 자리에서
   * 이어본다**(`receiver.resumeRecorded`). `select` 로 대신하면 처음부터가 된다.
   */
  | { kind: 'resumeRecorded' }
  /** 같은 채널의 다른 항목 — `pickProduct` 로 간다. **재생이 안 끊긴다** */
  | { kind: 'select'; productId: string }
  /** 다른 채널. `startFromProductId` 가 `undefined` 면 「지금」 카드다 */
  | { kind: 'switch'; channelId: string; startFromProductId?: string };

export function resolveEpgPick(input: {
  /** 지금 재생 중인 채널. 편성표가 전체화면(재생 전)이면 `null` */
  currentChannelId: string | null;
  /** 지금 재생 중인 항목 */
  currentProductId: string | null;
  pickedChannelId: string;
  pickedProductId: string;
  /** 그 카드가 그 행의 **첫 장**인가 — 「지금」 카드다 */
  isEntryCard: boolean;
  /**
   * v33-b. **지금 그 채널이 바이패스인가**(`receiver.getLive() !== null`). 화면이 아는
   * 사실이고, 이 함수는 그것으로 「같은 것」과 「돌아간다」를 가른다 — 아래 ★★.
   */
  inLive?: boolean;
}): EpgPick {
  const {
    currentChannelId,
    currentProductId,
    pickedChannelId,
    pickedProductId,
    isEntryCard,
    inLive = false,
  } = input;

  /*
   * ★ v33-b. **라이브 상자는 회차를 가리키지 않는다** — `productId` 가 빈 문자열인 것이
   * 그 표시다(절대 규칙 68 의 단서 · 빈 문자열은 어떤 회차의 id 도 아니다).
   */
  const isLiveCard = pickedProductId === '';

  if (currentChannelId !== null && pickedChannelId === currentChannelId) {
    // 같은 채널이다. 여기서 `switchTo` 를 부르면 `next === from` 가드에 걸려 **아무 일도
    // 일어나지 않고**, 가드를 풀면 리시버가 통째로 다시 서서 **영상이 끊긴다**.
    if (isLiveCard) return { kind: 'live' };
    if (currentProductId !== null && pickedProductId === currentProductId) {
      /*
       * ★★ **바이패스 중이면 「같은 것」이 아니다** (2026-09-22 무대 신고).
       *
       * `getCurrentItem()` 은 바이패스 중에도 녹화 회차를 돌려준다(절대 규칙 83) —
       * 그래서 **화면에는 라이브가 나오는데** 이 판정만 보면 「이미 그것을 보고 있다」가
       * 되어 **아무 일도 일어나지 않았다.** 시청자가 누른 것은 「라이브를 두고 내
       * 콘텐츠로」이고, 돌아갈 자리는 창이 끝났을 때와 **같은 값**이어야 한다(규칙 84).
       */
      return inLive ? { kind: 'resumeRecorded' } : { kind: 'same' };
    }
    return { kind: 'select', productId: pickedProductId };
  }

  // 다른 채널. **첫 장이면 회차를 가리키지 않는다** — 라이브 상자도 여기다(회차가 없다).
  return isEntryCard || isLiveCard
    ? { kind: 'switch', channelId: pickedChannelId }
    : { kind: 'switch', channelId: pickedChannelId, startFromProductId: pickedProductId };
}

/**
 * 「로그인하면 이어집니다」를 그릴 것인가 (2026-08-31 실기 뒤 신설).
 *
 * ★ **둘 다여야 한다.**
 *
 * ① **로그인하지 않았을 때만.** 안 보면 **로그인한 시청자에게 「로그인하세요」라고
 *    말한다** — 배포된 채로 그랬다. 그리고 그 문장은 **틀린 것이 아니라 무의미해서**
 *    아무도 오류로 신고하지 않는다.
 * ② **일반 채널이 하나라도 있을 때만.** 홈쇼핑에는 이어보기가 **원리적으로 없으므로**
 *    (`resolveResume` 이 언제나 `null` 이다 — D5) 그 배포에서는 이 문장이 **거짓**이다.
 *
 * **문구가 적용 범위를 스스로 말하게 한 것도 같은 이유다** (`epg.signedOutHint` 가
 * 「드라마 채널을」이라고 말한다) — 편성표는 두 종류를 섞어 보이고, 즐겨찾기 때문에
 * **홈쇼핑 행이 맨 아래로 올 수 있다.** 그러면 이 한 줄이 바로 그 행에 붙은 것처럼
 * 읽힌다 — 2026-08-31 무대에서 그렇게 보였다.
 */
export function showsSignInHint(rows: readonly EpgRow[], signedIn: boolean): boolean {
  if (signedIn) return false;
  return rows.some((row) => hasResume(row.channel));
}

/**
 * 그 채널에 **이어보기가 있는가** (v28). 「일반 채널인가」를 대신한다.
 *
 * ── ★ 축이 둘이 되며 갈린 자리다 ────────────────────────────
 *
 * ONAIR 채널은 `kind: 'general'` 이지만 **언제나 라이브**라 두고 온 자리가 없다
 * (절대 규칙 76). `kind` 만 보면 —
 *
 * - **`showsSignInHint`** 가 ONAIR 뿐인 배포에서 「로그인하면 이어집니다」를 그린다.
 *   그 문장은 **틀린 것이 아니라 거짓**이고, 로그인해 봐도 아무 일이 없다
 * - **전환 시 `select` 진행 보고**가 ONAIR 채널로 나간다. 서버가 202 로 버리지만
 *   (`docs/57` §9) **보내는 쪽도 알고 있어야** 그 조용함이 자물쇠 하나에 안 매인다
 *
 * `watch-progress.ts` 의 `isGeneral` 이 정확히 이 뜻이었다 — 이름이 축을 가리켰을 뿐이다.
 */
export function hasResume(channel: Pick<ChannelSummary, 'kind' | 'delivery'>): boolean {
  return channel.kind === 'general' && channel.delivery === 'receiver';
}

/**
 * 채널 재핑 — **다음/이전 채널** (v25).
 *
 * ── 순서는 채널 목록 그대로다 ────────────────────────────────
 *
 * `rows` 는 `GET /v1/channels` 의 순서(`createdAt asc`)이고, **즐겨찾기는 그것을 바꾸지
 * 않는다** — 별은 편성표의 **표시**를 가르는 것이지 채널의 순서가 아니다. 재핑이
 * 즐겨찾기 순서를 타면 **별 하나를 누른 뒤 채널 번호가 통째로 바뀌는** 셈이고, 그것은
 * 리모컨의 ▲▼ 가 뜻하는 바가 아니다.
 *
 * ── 고리다 ──────────────────────────────────────────────────
 *
 * 마지막에서 ▲ 는 처음으로, 처음에서 ▼ 는 마지막으로 돈다 — 리시버가 회차를
 * `(index + 1) % length` 로 도는 것과 같은 성질이다. **끝에서 아무 일도 안 일어나면**
 * 「리모컨이 죽었다」와 구분되지 않는다.
 *
 * 지금 채널이 목록에 없으면 `null` 이다 — 편성이 그 사이에 바뀌어 그 채널이 빠진
 * 경우이고, 그때 아무 데로나 옮기는 것보다 **아무 일도 하지 않는 것**이 낫다.
 */
export function neighborChannel(
  rows: readonly EpgRow[],
  currentChannelId: string,
  step: 1 | -1,
): string | null {
  if (rows.length < 2) return null;
  const at = rows.findIndex((row) => row.channel.channelId === currentChannelId);
  if (at < 0) return null;
  const next = (at + step + rows.length) % rows.length;
  return rows[next]?.channel.channelId ?? null;
}

/** 카드가 이어보기 지점을 그릴 자리인가 — 그 채널에 **딱 한 장**이다 */
export function resumeMarkIndex(lineup: ChannelLineup | null): number | null {
  if (lineup === null || lineup.resumePositionSec === null) return null;
  // 회전된 첫 장이 곧 두고 온 회차다. 서버가 그렇게 만든다 (절대 규칙 68).
  return lineup.items.length > 0 ? 0 : null;
}

/** 카드 하나의 회차 라벨용 — **인덱스로 「3회」를 그리면 회전 때문에 거짓말이 된다** */
export function episodeNoOf(item: LineupItem): number | null {
  return item.content?.episodeNo ?? null;
}

/**
 * 편성표에서 **「보는 중」을 붙일 채널** (2026-09-06 무대 신고).
 *
 * ── 왜 순수 함수인가 ────────────────────────────────────────
 *
 * 부르는 자리가 셋인데(`WatchScreen`·`OnairScreen`·`ChannelGate`) **각자 자기 채널
 * id 를 그냥 넘기고 있었다.** 그 셋 중 둘은 **재생이 아직 시작되지 않은 상태에서도**
 * 열린다 — 진입 게이트에서 연 편성표와, 채널 목록(`/channels`)의 전체화면 편성표다.
 * 그래서 **아무것도 재생되고 있지 않은데 1번 채널에 「보는 중」이 붙었다.**
 *
 * 규칙이 「근거 없는 표시는 틀린 것보다 나쁘다」고 적은 그 자리다(절대 규칙 46) —
 * 시청자는 그것을 보고 **자기가 무엇을 보다 왔는지**를 판단한다.
 *
 * ★ **`channelId` 의 유무로 가르지 말 것.** 게이트에도 채널은 정해져 있다(그 채널을
 * 「틀 참」이다) — 갈리는 것은 **틀었는가** 하나이고, 그것이 `started` 다.
 */
export function watchingChannelId(input: {
  /** 이 화면이 맡은 채널. 게이트에서도 정해져 있다 */
  channelId: string;
  /** **재생이 한 번이라도 시작됐는가.** 게이트가 서 있으면 거짓이다 */
  started: boolean;
}): string | null {
  return input.started ? input.channelId : null;
}

/**
 * 편성표에서 **다른 채널을 고른 뒤**, 그 시트를 언제 닫을 것인가 (2026-09-06 무대 신고).
 *
 * ── 무엇이 문제였나 ─────────────────────────────────────────
 *
 * 고르는 순간 시트를 닫고 전환을 걸었다. 그런데 전환에는 시간이 걸리고, 그 사이를
 * **잡아 둔 마지막 프레임**(v28)이 덮는다 — 그것은 **방금 떠나기로 한 채널의 그림**이다.
 * 그래서 시청자에게는 「눌렀더니 원래 채널이 한 번 더 나왔다가 옮겨간다」로 보였다.
 *
 * **고침은 반대 방향이다** — 시트를 **더 오래 둔다.** 편성표에 머무르다 새 채널이 실제로
 * 화면에 나오는 순간 걷힌다. 그러면 옛 채널의 그림이 **한 프레임도 안 보인다.**
 *
 * ★ **「전환이 시작됐다」로 닫지 말 것.** 그때는 아직 새 채널이 아무것도 안 그린다 —
 * 닫으면 정확히 지금의 증상으로 돌아간다. 닫는 근거는 **새 채널이 보이는가** 하나다.
 */
export function closesSwitchEpg(input: {
  /** 새 채널이 재생 중인가 (`state === 'playing'`) */
  showing: boolean;
  /** 잡아 둔 프레임이 아직 덮고 있는가. **덮고 있으면 그것은 옛 채널의 그림이다** */
  frozen: boolean;
  /** 새 채널이 실패했는가. **시트가 오류 스크림을 덮으면 안 된다** */
  failed: boolean;
}): boolean {
  if (input.failed) return true;
  return input.showing && !input.frozen;
}
